import i18next from "i18next";
import { DEFAULT_LOCALE, intlLocaleFor } from "../i18n/locale";

/**
 * The tag dates are formatted with. English keeps the browser's own region, as
 * it always has, so a reader in London still sees day before month; any other
 * language formats the way that language does.
 */
function formatLocale(): string | undefined {
  const language = i18next.language;
  return !language || language === DEFAULT_LOCALE ? undefined : intlLocaleFor(language);
}

const formatters = new Map<string, Intl.RelativeTimeFormat | Intl.DateTimeFormat>();

function cached<T extends Intl.RelativeTimeFormat | Intl.DateTimeFormat>(key: string, build: (locale: string | undefined) => T): T {
  const locale = formatLocale();
  const id = `${key}:${locale ?? ""}`;
  let formatter = formatters.get(id);
  if (!formatter) {
    formatter = build(locale);
    formatters.set(id, formatter);
  }
  return formatter as T;
}

const relative = () => cached("relative", (locale) => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }));
const absolute = () =>
  cached("absolute", (locale) => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }));
const dayOnly = () => cached("day", (locale) => new Intl.DateTimeFormat(locale, { dateStyle: "medium" }));

const DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: "second" },
  { amount: 60, unit: "minute" },
  { amount: 24, unit: "hour" },
  { amount: 7, unit: "day" },
  { amount: 4.34524, unit: "week" },
  { amount: 12, unit: "month" },
  { amount: Number.POSITIVE_INFINITY, unit: "year" },
];

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const timestamp = new Date(iso).getTime();
  if (!Number.isFinite(timestamp)) return "—";

  let duration = (timestamp - Date.now()) / 1000;
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return relative().format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return "—";
}

/**
 * A date with no clock on it.
 *
 * For facts whose hour means nothing — the day an account was created, the day
 * a rate was last checked. `absoluteTime` prints "Feb 11, 2026, 3:00 PM" for
 * those, and the time is noise a reader has to look past every time.
 */
export function absoluteDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? dayOnly().format(date) : "—";
}

export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? absolute().format(date) : "—";
}

/**
 * "1 Oct", in UTC.
 *
 * The one date in the product that is not the reader's day: it is the day the
 * inference ledger rolls over, which the API states in UTC and gates on in UTC.
 * Rendered in the reader's clock it would name the wrong day either side of the
 * boundary for most of the world.
 *
 * English pins `en-GB` rather than taking the browser's region, which every
 * other formatter here does — this string predates them and reads "1 Oct"
 * everywhere it appears.
 */
const clock = () => cached("clock", (locale) => new Intl.DateTimeFormat(locale, { timeStyle: "short" }));

/** The time of day alone, in the viewer's own zone — the row of a day-grouped list. */
export function clockTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? clock().format(date) : "—";
}

/** A local calendar day, as a sortable key: rows with the same key share a heading. */
export function localDayKey(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/**
 * A day heading: "Today" and "Yesterday" in the viewer's language — from
 * `Intl.RelativeTimeFormat` with `numeric: "auto"`, so Indonesian reads "hari
 * ini" and "kemarin" without a string of ours — and the date after that.
 */
export function dayHeading(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "—";
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(date) - startOf(now)) / 86_400_000);
  if (days === 0 || days === -1) {
    const word = relative().format(days, "day");
    return word.charAt(0).toLocaleUpperCase() + word.slice(1);
  }
  return dayOnly().format(date);
}

export function utcDayMonth(iso: string): string {
  return cached(
    "utcDayMonth",
    (locale) =>
      new Intl.DateTimeFormat(locale ?? "en-GB", {
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      }),
  ).format(new Date(iso));
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];

/** "1.2 GB" — an em dash for anything that was not recorded. */
export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${BYTE_UNITS[unit]}`;
}

/**
 * Milliseconds as `m:ss`, for a countdown that is ticking toward a deadline.
 *
 * Clamped at zero because the interval driving it fires on a timer: the browser
 * can hand back a negative remaining time on a tab that was suspended, and
 * `-1:-01` is worse than `0:00`. Minutes are not padded and seconds always are,
 * which is how a clock reads.
 */
export function countdown(remainingMs: number): string {
  const total = Math.ceil(Math.max(0, remainingMs) / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function duration(startIso: string | null, endIso: string | null): string {
  if (!startIso || !endIso) return "—";
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function describeSource(source: {
  type: string;
  owner?: string | null;
  repository?: string | null;
  branch?: string | null;
  url?: string | null;
  image?: string | null;
}): string {
  switch (source.type) {
    case "repo":
    case "github":
      return `${source.owner ?? "?"}/${source.repository ?? "?"}@${source.branch ?? "main"}`;
    case "git":
      return `${source.url ?? "?"}@${source.branch ?? "main"}`;
    case "docker":
      return source.image ?? "?";
    default:
      return "—";
  }
}
